import classNames from 'classnames';
import { add } from 'date-fns';
import React, { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getScheduleEventCompositeKey } from '~/helpers/scheduleEventKey';
import { PlannerViewData } from '~/routes/calendar._index';

export type EventDetails = {
  id: string;
  status: string;
  siteId: string;
  scheduledStart: Date;
  days: number;
  jobName: string;
  assigneeName: string;
  assigneeId?: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
};

// Alternative Job object format (for CalendarView compatibility)
export type Job = {
  id: string;
  summary: string;
  status: string;
  start: string;
  end?: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  assignee?: {
    id: string;
    name: string;
    image?: string | null;
  } | null;
  attributes?: Array<{
    id: string;
    type: string;
    category?: string | null;
    name: string;
    value: string[];
  }> | null;
};

// Align SiteData with PlannerViewData entries for consistent callback typing
export type SiteData = PlannerViewData[number];

type DraggableJobWrapperProps = {
  children: ReactNode;
  preview?: ReactNode; // Made optional for backward compatibility
  // Primary interface - use either eventDetails OR job
  eventDetails?: EventDetails;
  job?: Job;
  siteId?: string; // Required when using job format
  days?: number; // Optional override for job format
  // Event handlers
  onDragBegin?: () => void;
  onDragEnd?: () => void;
  onClick?: (eventStatus: string, eventId: string) => void;
  onMouseEnter?: (
    jobId: string,
    site: SiteData,
    date: Date,
    element: HTMLElement
  ) => void;
  onMouseLeave?: () => void;
  // Context data for hover dialogs
  siteData?: SiteData;
  columnIndex?: number;
  start?: Date;
  isMobile?: boolean;
  // Style overrides
  className?: string;
  style?: React.CSSProperties;
  // Advanced options
  enableTooltips?: boolean;
  enableHoverDialogs?: boolean;
  /** When true, hide the original element during drag so only the ghost/preview is visible */
  hideWhileDragging?: boolean;
};

// Preload a 1x1 transparent image for Firefox setDragImage (must be loaded before dragstart)
const TRANSPARENT_IMG: HTMLImageElement = (() => {
  if (typeof Image === 'undefined')
    return undefined as unknown as HTMLImageElement;
  const img = new Image();
  img.src =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/woAAr8B9u9cM0UAAAAASUVORK5CYII=';
  return img;
})();

// Helper to convert Job object to EventDetails
const jobToEventDetails = (
  job: Job,
  siteId: string,
  days: number = 1
): EventDetails => ({
  id: job.id,
  status: job.status,
  siteId,
  scheduledStart: new Date(job.start),
  days,
  jobName: job.summary,
  assigneeName: job.assignee?.name || '',
  assigneeId: job.assignee?.id,
  scheduledStartTime: job.scheduledStartTime,
  scheduledEndTime: job.scheduledEndTime,
});

// Helper for richer tooltip messages by status
const getNonDraggableTooltip = (status: string): string => {
  switch (status) {
    case 'InProgress':
      return 'Cannot move job that is in progress';
    case 'Complete':
      return 'Cannot move completed job';
    case 'Cancelled':
      return 'Cannot move cancelled job';
    case 'Declined':
      return 'Cannot move declined job';
    case 'Deleted':
      return 'Cannot move deleted job';
    case 'Incomplete':
      return 'Cannot move incomplete job';
    case 'Paused':
      return 'Cannot move paused job';
    case 'Booking':
      return 'Cannot move booking';
    default:
      return 'Cannot move this job';
  }
};

/**
 * Unified DraggableJobWrapper with all advanced behaviors:
 * - Drag functionality with tooltips and visual feedback
 * - Click handling for navigation
 * - Hover dialog support for PlannerView
 * - Support for both EventDetails and Job object formats
 * - Memory leak prevention and accessibility
 * - Flexible styling and positioning options
 *
 * Memoized to prevent unnecessary re-renders when parent scrolls/updates.
 */
export const DraggableJobWrapper: React.FC<DraggableJobWrapperProps> = memo(
  ({
    children,
    preview,
    eventDetails,
    job,
    siteId,
    days = 1,
    onDragBegin,
    onDragEnd,
    onClick,
    onMouseEnter,
    onMouseLeave,
    siteData,
    columnIndex = 0,
    start,
    isMobile = false,
    className,
    style,
    enableTooltips = true,
    enableHoverDialogs = true,
    hideWhileDragging = false,
  }) => {
    // Determine the event details to use (support both formats)
    const resolvedEventDetails =
      eventDetails ||
      (job && siteId ? jobToEventDetails(job, siteId, days) : null);

    if (!resolvedEventDetails) {
      throw new Error(
        'DraggableJobWrapper requires either eventDetails or (job + siteId)'
      );
    }

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const longClickTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hoverValidationTimerRef = useRef<NodeJS.Timeout | null>(null);

    const canDragJob =
      resolvedEventDetails.status === 'Created' ||
      resolvedEventDetails.status === 'Offered' ||
      resolvedEventDetails.status === 'Accepted';

    // Create default preview if not provided
    const defaultPreview = preview || (
      <div className='bg-blue-100 border-blue-300 rounded border px-2 py-1 text-xs'>
        {resolvedEventDetails.jobName}
      </div>
    );

    const [collectedProps, drag] = useDrag({
      type: 'job',
      item: () => {
        onDragBegin?.();
        return {
          eventId: resolvedEventDetails.id,
          siteId: resolvedEventDetails.siteId,
          scheduledStart: resolvedEventDetails.scheduledStart,
          days: resolvedEventDetails.days,
          jobPreview: defaultPreview,
          status: resolvedEventDetails.status,
          jobName: resolvedEventDetails.jobName,
          assigneeName: resolvedEventDetails.assigneeName,
          assigneeId: resolvedEventDetails.assigneeId,
          originalScheduledStartTime: resolvedEventDetails.scheduledStartTime,
          originalScheduledEndTime: resolvedEventDetails.scheduledEndTime,
        };
      },
      end: () => {
        onDragEnd?.();
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => canDragJob,
    });

    // Advanced event handlers
    const handleClick = (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick(resolvedEventDetails.status, resolvedEventDetails.id);
      }
    };

    const handleMouseEnterHover = (e: React.MouseEvent) => {
      // Handle hover dialogs (PlannerView feature)
      // Only trigger if the hover is on the actual pill content, not just the wrapper
      // Do not show dialog for booking events
      if (resolvedEventDetails.status === 'Booking') return;

      if (
        enableHoverDialogs &&
        !isMobile &&
        onMouseEnter &&
        siteData &&
        start
      ) {
        // Check if we're hovering over the pill content by validating the event target
        const target = e.target as HTMLElement;
        const currentTarget = e.currentTarget as HTMLElement;

        // Validate that we're hovering over actual pill content:
        // 1. Target must be within the current target (basic containment)
        // 2. Target should not be the wrapper itself (avoid empty area triggers)
        // 3. Target should have visible content (check for meaningful dimensions)
        if (target && currentTarget && currentTarget.contains(target)) {
          // Additional check: ensure we're not triggering on empty/transparent areas
          // by verifying the target element has actual content or is styled
          const targetRect = target.getBoundingClientRect();
          const hasVisibleContent =
            targetRect.width > 0 && targetRect.height > 0;

          // Also check if the target is the wrapper itself - if so, validate it has meaningful content
          const isWrapper = target === currentTarget;

          if (
            hasVisibleContent &&
            (!isWrapper || currentTarget.children.length > 0)
          ) {
            onMouseEnter(
              resolvedEventDetails.id,
              siteData,
              add(start, { days: columnIndex }),
              currentTarget
            );
          }
        }
      }
    };

    const handleMouseLeaveHover = () => {
      // Handle hover dialog cleanup
      if (enableHoverDialogs && !isMobile && onMouseLeave) {
        onMouseLeave();
      }
      // Also handle tooltip cleanup
      handleMouseLeaveTooltip();
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      // Handle tooltip for non-draggable items
      if (enableTooltips && !canDragJob) {
        setTooltipPosition({ x: e.clientX, y: e.clientY - 30 });
        longClickTimerRef.current = setTimeout(() => {
          setShowTooltip(true);
        }, 500);
      }
    };

    const handleMouseUp = () => {
      if (longClickTimerRef.current) {
        clearTimeout(longClickTimerRef.current);
        longClickTimerRef.current = null;
      }
      setShowTooltip(false);
    };

    const handleMouseLeaveTooltip = () => {
      if (longClickTimerRef.current) {
        clearTimeout(longClickTimerRef.current);
        longClickTimerRef.current = null;
      }
      setShowTooltip(false);
    };

    const isMounted = useRef(true);
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
        if (longClickTimerRef.current) {
          clearTimeout(longClickTimerRef.current);
        }
        if (hoverValidationTimerRef.current) {
          clearTimeout(hoverValidationTimerRef.current);
        }
      };
    }, []);

    return (
      <>
        <div
          key={getScheduleEventCompositeKey(resolvedEventDetails)}
          ref={drag}
          className={classNames(
            'touch-manipulation select-none transition-opacity',
            {
              'opacity-50': collectedProps.isDragging && !hideWhileDragging,
              'cursor-pointer': canDragJob,
            },
            className
          )}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            touchAction: 'manipulation',
            // Ensure underlying drop zones can receive hover/drop while dragging
            pointerEvents: collectedProps.isDragging ? 'none' : 'auto',
            visibility:
              collectedProps.isDragging && hideWhileDragging
                ? 'hidden'
                : undefined,
            ...style,
          }}
          onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
            // Firefox needs data set and effectAllowed to properly deliver dragover/drop
            try {
              if (e.dataTransfer) {
                // must be non-empty in FF to enable DnD
                e.dataTransfer.setData(
                  'text/plain',
                  JSON.stringify({ id: resolvedEventDetails.id })
                );
                e.dataTransfer.effectAllowed = 'move';

                // In Firefox, setting a tiny transparent drag image helps ensure hover events propagate
                const isFirefox =
                  typeof navigator !== 'undefined' &&
                  /firefox/i.test(navigator.userAgent);
                if (isFirefox) {
                  if (TRANSPARENT_IMG && TRANSPARENT_IMG.complete) {
                    e.dataTransfer.setDragImage(TRANSPARENT_IMG, 0, 0);
                  }
                }
              }
            } catch (error) {
              console.error('Error setting drag data:', error);
            }
          }}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseEnter={handleMouseEnterHover}
          onMouseLeave={handleMouseLeaveHover}
        >
          {children}
        </div>

        {enableTooltips && showTooltip && !canDragJob && (
          <div
            className='pointer-events-none fixed z-[1000] rounded border-outline-default bg-gray-800 px-2 py-1 text-xs text-white'
            style={{ left: tooltipPosition.x, top: tooltipPosition.y }}
          >
            {getNonDraggableTooltip(resolvedEventDetails.status)}
          </div>
        )}
      </>
    );
  }
);

DraggableJobWrapper.displayName = 'DraggableJobWrapper';

export default DraggableJobWrapper;
